import { Component, OnInit, HostListener, Inject, LOCALE_ID } from '@angular/core';
import { KPIService, HistoryReturn } from 'src/services/kpi/kpi.service';
import { ContactService } from 'src/services/contact/contact.service';
import { Contact } from 'src/classes/contact';
import { ColumnConfig } from '../filterable-paginated-table/filterable-paginated-table.component';

interface RowEventData {
  index: number;
  contactId: number;
  contactName: string;
  contactEmail: string;
  message: string;
  reportName: string;
  reportId: number;
  startDate: string;
  endDate: string;
  status: 'Sending' | 'Complete' | 'Error';
  lastOpen: string;
  countOpen: string;
}

@Component({
  selector: 'app-log-page',
  templateUrl: './log-page.component.html',
  styleUrls: ['./log-page.component.scss']
})
export class LogPageComponent implements OnInit {

  public error = {
    active: false,
    title: 'Unknown Error',
    message: 'An unknown error occured',
    stack: 'The delivery logs entered the error state incorretly',
    stackExpanded: false,
    options: 'none'
  };

  public columns: ColumnConfig[] = [
    {label: 'Index', property: ['index'], filter: true, hidden: true},
    {label: 'Status', property: ['status'], filter: true, width: "130px"},
    {label: 'Name', property: ['reportName'], filter: true},
    {label: 'Message', property: ['message'], filter: true, width: "25%"},
    {label: 'Date', property: ['startDate'], filter: true},
    {label: 'Last Open', property: ['lastOpen'], filter: true},
    {label: '#Open', property: ['countOpen'], filter: true},
    {label: 'Contact', property: ['contactName'], filter: true}
  ];

  public loading = false;
  public contacts: Contact[];
  public rows: RowEventData[] = [];

  constructor(
    private kpiService: KPIService,
    private contactService: ContactService,
    @Inject(LOCALE_ID) public locale: string
  ) { }

  ngOnInit() {
    this.fetchHistoryData();
    this.applyLocaleToHeader();
  }

  private applyLocaleToHeader() {
    if (typeof this.locale === "string" && this.locale.toLowerCase().substring(0, 2) === "pt") {
      const portugueseList = ["Index", "Status", "Nome", "Mensagem", "Data","Última Visualização", "#Vezes", "Contato"];

      portugueseList.forEach((portugueseLabel, index) => this.columns[index].label = portugueseLabel);
    }
  }

  onRefreshClick() {
    this.fetchHistoryData();
  }

  getContactById(id: number): Contact {
    if (!this.contacts) {
      return null;
    }
    for (const contact of this.contacts) {
      if (contact.idContact === id) {
        return contact;
      }
    }
    return null;
  }

  historyDataToRowEventData(history: HistoryReturn, index: number): RowEventData {
    const contactId = typeof history.idContact === 'number' ? history.idContact : parseInt(history.idContact, 10);
    const contact = this.getContactById(contactId);
    return {
      index: index,
      contactId: contactId,
      reportId: typeof history.idReport === 'number' ? history.idReport : parseInt(history.idReport, 10),
      contactName: contact ? contact.name : "unknown id" + (history.idContact ? " " + history.idContact : ""),
      contactEmail: contact ? contact.email : "unknown id" + (history.idContact ? " " + history.idContact : ""),
      reportName: history.name,
      message: history.message,
      startDate: history.startDate ? history.startDate.toLocaleString() : "No start date",
      lastOpen: history.lastOpen ? `${history.lastOpen.toDateString()} ${String(history.lastOpen.getHours()).padStart(2,"0")}:${String(history.lastOpen.getMinutes()).padStart(2,"0")}` : "No open yet",
      countOpen:  history.countOpen  ? history.countOpen :'0',
      endDate: history.endDate ? history.endDate.toLocaleString() : "",
      status: history.historyType
    }
  }

  async fetchHistoryData() {
    this.loading = true;
    try {
      const [data, contacts] = await Promise.all([
        this.loadHistoryData(),
        this.loadContactData()
      ]);
      this.contacts = contacts;
      this.rows = data.reverse().map(this.historyDataToRowEventData.bind(this));
    } catch(err) {
      this.error.title = 'Error while loading history data';
      this.error.message = err.message;
      this.error.stack = err.stack;
      this.error.active = true;
    }
    this.loading = false;
  }

  async loadContactData() {
    const contactResult = await this.contactService.listContacts();
    return contactResult.data;
  }

  async loadHistoryData() {
    try {
      const data = await this.kpiService.getHistory();
      return data;
    } catch(err) {
      console.log(err);
    }
  }
}
